import axios from 'axios'

export interface Category {
  category_id: string
  name: string
  description: string
  parent_category_id: string | null
  is_active: boolean
  logo_url: string | null
  created_at: string
}

export interface CreateCategoryDto {
  name: string
  description?: string
  parent_category_id?: string | null
  logo?: File | null
}

export interface UpdateCategoryDto {
  name?: string
  description?: string
  parent_category_id?: string | null
  is_active?: boolean
  logo?: File | null
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  success: boolean
}

export interface CategoryFilters {
  parent_category_id?: string
  is_active?: boolean
  search?: string
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const categoryApi = axios.create({
  baseURL: `${API_BASE_URL}/categories`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
categoryApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
categoryApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const categoryService = {
  // Get all categories with optional filters
  async getAll(filters: CategoryFilters = {}): Promise<PaginatedResponse<Category>> {
    const params = new URLSearchParams()
    
    if (filters.parent_category_id !== undefined) params.append('parent_category_id', filters.parent_category_id)
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString())
    if (filters.search) params.append('search', filters.search)

    const response = await categoryApi.get('', { params })
    return response.data
  },

  // Get category by ID
  async getById(id: string): Promise<{ success: boolean; data: Category }> {
    const response = await categoryApi.get(`/${id}`)
    return response.data
  },

  // Get subcategories for a category
  async getSubcategories(id: string): Promise<PaginatedResponse<Category>> {
    const response = await categoryApi.get(`/${id}/subcategories`)
    return response.data
  },

  // Create new category (supports multipart with logo file)
  async create(categoryData: CreateCategoryDto): Promise<{ success: boolean; data: Category; message: string }> {
    const formData = new FormData()
    formData.append('name', categoryData.name)
    if (categoryData.description) formData.append('description', categoryData.description)
    if (categoryData.parent_category_id !== undefined && categoryData.parent_category_id !== null) {
      formData.append('parent_category_id', categoryData.parent_category_id)
    }
    if (categoryData.logo) {
      formData.append('logo', categoryData.logo)
    }

    const response = await categoryApi.post('', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Update category (supports multipart with optional logo file)
  async update(id: string, categoryData: UpdateCategoryDto): Promise<{ success: boolean; data: Category; message: string }> {
    const formData = new FormData()
    if (categoryData.name !== undefined) formData.append('name', categoryData.name)
    if (categoryData.description !== undefined) formData.append('description', categoryData.description)
    if (categoryData.parent_category_id !== undefined) {
      formData.append('parent_category_id', categoryData.parent_category_id === null ? '' : categoryData.parent_category_id)
    }
    if (categoryData.is_active !== undefined) formData.append('is_active', String(categoryData.is_active))
    if (categoryData.logo) {
      formData.append('logo', categoryData.logo)
    }

    const response = await categoryApi.put(`/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Delete category (soft delete)
  async delete(id: string): Promise<{ success: boolean; message: string }> {
    const response = await categoryApi.delete(`/${id}`)
    return response.data
  },

  // Get category statistics
  async getStatistics(): Promise<{
    total: number
    active: number
    inactive: number
    rootCategories: number
    subcategories: number
  }> {
    const allCategories = await this.getAll()
    const categories = allCategories.data
    
    const stats = {
      total: categories.length,
      active: categories.filter(c => c.is_active).length,
      inactive: categories.filter(c => !c.is_active).length,
      rootCategories: categories.filter(c => c.parent_category_id === null).length,
      subcategories: categories.filter(c => c.parent_category_id !== null).length
    }
    
    return stats
  }
}

export default categoryService
